/**
 * The call tab: may we ring, what to say, and what came of it.
 *
 * THE VERDICT IS SHOWN BEFORE ANY BUTTON IS PRESSED, the same way the letter tab
 * shows its own. On `blocked` the generate button is not disabled, it is absent:
 * a greyed-out button is an invitation to look for the way around it.
 *
 * THE VERDICT HERE IS NOT THE LETTER'S VERDICT and the copy has to say so.
 * A sole trader who cannot lawfully be written to can be rung - a live voice is
 * not what ESS section 103-1 regulates - so the same company can read "blocked"
 * on one tab and "call them, and say where you got the number" on this one. Two
 * banners that looked alike would make that difference invisible.
 *
 * A folder rather than one file from the first commit (AGENTS.md section 4): the
 * letter tab it is modelled on is already 383 lines with one text box, and this
 * one has five editable sections, a price table and an outcome row.
 */
import { Ban, Loader2, PenLine, Phone, RotateCcw, Save } from "lucide-react";
import { Button } from "@wa/ui";
import { StatusPill, useToast } from "../../../../ui";
import type { T } from "../../../site-scanner/types";
import type { BrainFit } from "../../../../../lib/siteBrain";
import type {
  BrainCallLegalView,
  BrainCallOutcome,
  BrainCallScript,
  BrainCallTouch,
  BrainFreshnessLike,
} from "../../../../../lib/siteBrainScript";
import { PackageLadder } from "./PackageLadder";
import { ScriptSections } from "./ScriptSections";
import { useScript } from "./useScript";

/** The outcomes the row offers, in the order a call can end. */
const OUTCOMES: BrainCallOutcome[] = ["no_answer", "gatekeeper", "reached", "callback", "meeting", "refused"];

export function BrainScript({
  domain,
  canManage,
  script,
  touch,
  fit,
  hasSynthesis,
  onChanged,
  t,
}: {
  domain: string;
  canManage: boolean;
  script: BrainCallScript | null;
  touch: BrainCallTouch | null;
  fit: BrainFit | null;
  hasSynthesis: boolean;
  onChanged: () => void;
  t: T;
}) {
  const toast = useToast();
  const state = useScript({ domain, script, storedTouch: touch, onChanged, toast, t });
  const blocked = state.legal?.state === "blocked";

  return (
    <div className="space-y-4">
      <LegalBanner legal={state.legal} t={t} />

      {!hasSynthesis ? <p className="text-sm text-muted-foreground">{t("brain.script.needsSynthesis")}</p> : null}

      {canManage && hasSynthesis && !blocked ? (
        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" variant="outline" onClick={() => void state.write(false)} disabled={state.busy}>
            {state.busy ? <Loader2 className="size-3.5 animate-spin" /> : <PenLine className="size-3.5" />}
            {t(script ? "brain.script.rewrite" : "brain.script.write")}
          </Button>

          {/* Only offered when there is something to save. A save button that is
              always there teaches the operator that pressing it does nothing. */}
          {state.dirty ? (
            <>
              <Button size="sm" onClick={() => void state.save()} disabled={state.busy}>
                <Save className="size-3.5" />
                {t("brain.script.save")}
              </Button>
              <Button size="sm" variant="ghost" onClick={state.discard} disabled={state.busy}>
                <RotateCcw className="size-3.5" />
                {t("brain.script.discard")}
              </Button>
            </>
          ) : null}

          <Button size="sm" variant="ghost" onClick={() => void state.refuse()} disabled={state.busy}>
            <Ban className="size-3.5" />
            {t("brain.script.refuse")}
          </Button>
        </div>
      ) : null}

      {/* Said before the operator dials, not after: a rewrite discards what they
          have typed, and finding that out afterwards is finding it out too late. */}
      {state.dirty ? <p className="crm-alert">{t("brain.script.unsavedWarning")}</p> : null}

      {state.stale ? (
        <StaleNotice freshness={state.stale} busy={state.busy} onConfirm={() => void state.write(true)} t={t} />
      ) : null}

      {script ? (
        <>
          <ScriptSections
            script={script}
            drafts={state.drafts}
            canManage={canManage}
            onDraft={state.setDraft}
            t={t}
          />
          <PackageLadder
            packages={script.packages}
            lead={fit?.segment === "A" ? "AGENTUUR" : fit ? "ÄRI" : null}
            t={t}
          />
          <Provenance script={script} t={t} />
        </>
      ) : null}

      {hasSynthesis ? (
        <OutcomeRow
          touch={state.touch}
          canManage={canManage}
          busy={state.busy}
          onPick={(value) => void state.setOutcome(value)}
          t={t}
        />
      ) : null}
    </div>
  );
}

/** Whether we may ring, and what the opening therefore has to say. */
function LegalBanner({ legal, t }: { legal: BrainCallLegalView | null; t: T }) {
  if (!legal) return null;
  const blocked = legal.state === "blocked";

  return (
    <section
      className="space-y-1 rounded-2xl bg-[var(--crm-sunk)] px-4 py-3"
      style={{
        boxShadow: `inset 3px 0 0 0 color-mix(in oklab, ${
          blocked ? "var(--color-destructive)" : "var(--color-success)"
        } 70%, transparent)`,
      }}
    >
      <div className="flex flex-wrap items-center gap-2">
        {blocked ? (
          <Ban className="size-4 shrink-0" style={{ color: "var(--color-destructive)" }} />
        ) : (
          <Phone className="size-4 shrink-0" style={{ color: "var(--color-success)" }} />
        )}
        <span className="text-sm font-semibold">{t(`brain.script.legal.${legal.state}`)}</span>
        {legal.number ? <StatusPill tone="mut">{legal.number}</StatusPill> : null}
        {legal.needsSourceLine ? <StatusPill tone="wt">{t("brain.script.sourceLineNeeded")}</StatusPill> : null}
      </div>

      <p className="text-xs text-muted-foreground">{t(`brain.script.reason.${legal.reason}`)}</p>

      {legal.suppression ? (
        <p className="text-xs text-muted-foreground">
          {t("brain.script.refusedOn", {
            value: legal.suppression.value,
            date: new Date(legal.suppression.at).toLocaleDateString(),
          })}
        </p>
      ) : null}
    </section>
  );
}

/** The proofs that no longer hold, and the one decision they force. */
function StaleNotice({
  freshness,
  busy,
  onConfirm,
  t,
}: {
  freshness: BrainFreshnessLike;
  busy: boolean;
  onConfirm: () => void;
  t: T;
}) {
  const dead = freshness.checks.filter((c) => c.verdict === "stale");
  return (
    <section className="space-y-2 rounded-xl border border-[var(--color-warning)]/40 bg-[var(--crm-sunk)] px-4 py-3">
      <p className="text-sm font-medium">
        {t(dead.length ? "brain.script.staleEvidence" : "brain.script.staleSynthesis")}
      </p>
      {dead.length ? (
        <ul className="space-y-0.5 text-xs text-muted-foreground">
          {dead.map((check) => (
            <li key={check.findingId}>
              {check.url ?? check.findingId}
              {check.detail ? ` (${check.detail})` : ""}
            </li>
          ))}
        </ul>
      ) : null}
      {/* Confirming is permission to work from an older run, never permission to
          open a call on a disproven claim: the server drops those either way. */}
      <Button size="sm" variant="outline" onClick={onConfirm} disabled={busy}>
        {t("brain.script.writeAnyway")}
      </Button>
    </section>
  );
}

/** Where these words came from, and whether a person has been through them. */
function Provenance({ script, t }: { script: BrainCallScript; t: T }) {
  return (
    <p className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
      <span>{t("brain.script.generatedOn", { date: new Date(script.generatedAt).toLocaleString() })}</span>
      {script.editedAt ? (
        <StatusPill tone="ok">
          {t("brain.script.editedOn", { date: new Date(script.editedAt).toLocaleString() })}
        </StatusPill>
      ) : (
        // Worth saying out loud. Everything above is a model's guess about a
        // company nobody here has spoken to, and the operator is about to read
        // it to a stranger.
        <StatusPill tone="wt">{t("brain.script.notReviewed")}</StatusPill>
      )}
    </p>
  );
}

/** What came of the call. The one number this ring is measured by. */
function OutcomeRow({
  touch,
  canManage,
  busy,
  onPick,
  t,
}: {
  touch: BrainCallTouch | null;
  canManage: boolean;
  busy: boolean;
  onPick: (outcome: BrainCallOutcome) => void;
  t: T;
}) {
  return (
    <section className="space-y-2 rounded-xl border border-dashed border-border px-4 py-3">
      <div className="flex flex-wrap items-baseline gap-2">
        <p className="text-sm font-medium">{t("brain.script.outcome")}</p>
        {touch ? (
          <span className="text-xs text-muted-foreground">
            {t("brain.script.calledOn", { date: new Date(touch.calledAt).toLocaleString() })}
          </span>
        ) : (
          <span className="text-xs text-muted-foreground">{t("brain.script.notCalled")}</span>
        )}
      </div>

      {canManage ? (
        <div className="flex flex-wrap items-center gap-2">
          {OUTCOMES.map((value) => (
            <button
              key={value}
              type="button"
              disabled={busy}
              onClick={() => onPick(value)}
              className={
                value === touch?.outcome
                  ? "rounded-full bg-primary px-3 py-1 text-xs font-medium text-primary-foreground"
                  : "rounded-full border border-border px-3 py-1 text-xs text-muted-foreground hover:text-foreground"
              }
            >
              {t(`brain.script.outcomeLabel.${value}`)}
            </button>
          ))}
        </div>
      ) : touch?.outcome ? (
        <StatusPill tone="mut">{t(`brain.script.outcomeLabel.${touch.outcome}`)}</StatusPill>
      ) : null}
    </section>
  );
}
